import React, { useEffect, useMemo, useRef, useState } from "react";
import PartyQuestPrize from "./PartyQuestPrize";
import { rescueEmoji } from "../utils/emojiRescue";

const DEFAULT_BACKEND =
  (typeof window !== "undefined" && window.__PARTY_BACKEND__) ||
  "https://partybutton-private-a027fa6fce78.herokuapp.com";


function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not available."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      reject,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000,
      }
    );
  });
}


function formatDistance(meters) {
  const value = Number(meters);

  if (!Number.isFinite(value)) return "";

  if (value < 160) {
    return `${Math.max(1, Math.round(value * 3.28084))} ft`;
  }

  const miles = value / 1609.344;

  return miles < 10
    ? `${miles.toFixed(1)} mi`
    : `${Math.round(miles)} mi`;
}


export default function PartyQuestSheet({
  backendBase,
  partyUid,
  onClose,
  onOpenUrl,
}) {
  const questEmojiRootRef = useRef(null);
  const base =
    String(backendBase || DEFAULT_BACKEND).replace(/\/+$/, "");

  const [quests, setQuests] = useState([]);
  const [quest, setQuest] = useState(null);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");


  const request = async (path, options = {}) => {
    const response = await fetch(`${base}${path}`, {
      ...options,
      headers: {
        ...(options.body
          ? { "Content-Type": "application/json" }
          : {}),
        "X-Party-Uid": partyUid,
        ...(options.headers || {}),
      },
      cache: "no-store",
    });

    let data = null;

    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const requestError = new Error(
        data?.error || `Request failed (${response.status})`
      );

      requestError.status = response.status;
      requestError.data = data;

      throw requestError;
    }

    return data;
  };


  // PB_PARTY_QUEST_ANALYTICS_V1
  const trackQuestAnalytics = (
    eventType,
    targetQuest = null,
    extra = {}
  ) => {
    if (!eventType) return;

    const target =
      targetQuest ||
      quest ||
      {};

    request(
      "/api/analytics/event",
      {
        method: "POST",
        body: JSON.stringify({
          eventType,

          source:
            window.Capacitor?.isNative
              ? "ios"
              : "web",

          questSlug:
            String(
              target?.slug || ""
            ),

          questTitle:
            String(
              target?.title || ""
            ),

          neighborhood:
            String(
              target?.neighborhood || ""
            ),

          ...extra,
        }),
      }
    ).catch((analyticsError) => {
      console.warn(
        "Party Quest analytics failed:",
        eventType,
        analyticsError?.message ||
          analyticsError
      );
    });
  };


  const loadQuests = async () => {
    setLoading(true);
    setError("");
    setNotice("");

    try {
      const data = await request("/api/quests");

      setQuests(
        Array.isArray(data?.quests)
          ? data.quests
          : []
      );
    } catch (requestError) {
      console.warn(
        "Party Quest catalog failed:",
        requestError
      );

      setError(
        "Party Quests could not be loaded right now."
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadQuests();

    trackQuestAnalytics(
      "party_quest_open"
    );

    // Party UID and backend base are stable for this sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const openQuest = async (item) => {
    const slug = String(item?.slug || "").trim();

    if (!slug) return;

    setActionLoading(true);
    setError("");
    setNotice("");

    try {
      const data = await request(
        `/api/quests/${encodeURIComponent(slug)}`
      );

      const openedQuest =
        data?.quest ||
        item ||
        null;

      setQuest(openedQuest);

      trackQuestAnalytics(
        "party_quest_view",
        openedQuest
      );
    } catch (requestError) {
      console.warn(
        "Party Quest detail failed:",
        requestError
      );

      setError(
        requestError?.message ||
        "This Party Quest could not be opened."
      );
    } finally {
      setActionLoading(false);
    }
  };


  const startQuest = async () => {
    if (!quest?.slug) return;

    setActionLoading(true);
    setError("");
    setNotice("");

    try {
      const data = await request(
        `/api/quests/${encodeURIComponent(quest.slug)}/start`,
        {
          method: "POST",
          body: JSON.stringify({}),
        }
      );

      const nextQuest =
        data?.quest ||
        quest;

      setQuest(nextQuest);

      if (!data?.resumed) {
        trackQuestAnalytics(
          "party_quest_start",
          nextQuest
        );
      }

      setNotice(
        data?.resumed
          ? "Quest resumed."
          : "Quest started. Your first stop is ready."
      );
    } catch (requestError) {
      console.warn(
        "Party Quest start failed:",
        requestError
      );

      setError(
        requestError?.message ||
        "The quest could not be started."
      );
    } finally {
      setActionLoading(false);
    }
  };


  const abandonQuest = async () => {
    if (!quest?.slug) return;

    const confirmed = window.confirm(
      "Abandon this Party Quest? Your completed stops will stay in your quest history."
    );

    if (!confirmed) return;

    setActionLoading(true);
    setError("");
    setNotice("");

    try {
      const data = await request(
        `/api/quests/${encodeURIComponent(quest.slug)}/abandon`,
        {
          method: "POST",
          body: JSON.stringify({}),
        }
      );

      const nextQuest =
        data?.quest ||
        quest;

      setQuest(nextQuest);

      trackQuestAnalytics(
        "party_quest_abandon",
        nextQuest
      );

      setNotice("Quest abandoned.");
    } catch (requestError) {
      console.warn(
        "Party Quest abandon failed:",
        requestError
      );

      setError(
        requestError?.message ||
        "The quest could not be abandoned."
      );
    } finally {
      setActionLoading(false);
    }
  };


  const restartQuest = async () => {
    if (!quest?.slug) return;

    setActionLoading(true);
    setError("");
    setNotice("");

    try {
      const data = await request(
        `/api/quests/${encodeURIComponent(quest.slug)}/restart`,
        {
          method: "POST",
          body: JSON.stringify({}),
        }
      );

      const nextQuest =
        data?.quest ||
        quest;

      setQuest(nextQuest);

      trackQuestAnalytics(
        "party_quest_restart",
        nextQuest
      );

      setNotice("New quest started.");
    } catch (requestError) {
      console.warn(
        "Party Quest restart failed:",
        requestError
      );

      setError(
        requestError?.message ||
        "The quest could not be restarted."
      );
    } finally {
      setActionLoading(false);
    }
  };


  const checkIn = async () => {
    if (!quest?.slug) return;

    setActionLoading(true);
    setError("");
    setNotice("Checking your location…");

    try {
      const location = await getCurrentPosition();

      const data = await request(
        `/api/quests/${encodeURIComponent(quest.slug)}/checkin`,
        {
          method: "POST",
          body: JSON.stringify(location),
        }
      );

      const nextQuest =
        data?.quest ||
        quest;

      setQuest(nextQuest);

      trackQuestAnalytics(
        "party_quest_checkin",
        nextQuest,
        {
          questStopOrder:
            Number(
              data?.completedStop?.order
            ) || null,
        }
      );

      if (data?.questCompleted) {
        trackQuestAnalytics(
          "party_quest_complete",
          nextQuest
        );
        setNotice(
          `${data?.badge?.emoji || "🏆"} ${
            data?.badge?.name || "Quest complete!"
          }`
        );
      } else {
        setNotice(
          `${data?.completedStop?.name || "Stop"} complete! Next stop unlocked.`
        );
      }
    } catch (requestError) {
      console.warn(
        "Party Quest check-in failed:",
        requestError
      );

      if (
        requestError?.data?.code ===
        "NOT_CLOSE_ENOUGH"
      ) {
        const distance = formatDistance(
          requestError?.data?.distanceMeters
        );

        setError(
          distance
            ? `You're still ${distance} from this stop. Get within about 100 meters to check in.`
            : "You're not close enough to this stop yet."
        );
      } else if (
        requestError?.code === 1 ||
        requestError?.message?.toLowerCase?.().includes(
          "permission"
        )
      ) {
        setError(
          "Location permission is required to check in."
        );
      } else {
        setError(
          requestError?.message ||
          "Check-in could not be completed."
        );
      }

      setNotice("");
    } finally {
      setActionLoading(false);
    }
  };


  useEffect(() => {
    const runEmojiRescue = () => {
      if (questEmojiRootRef.current) {
        rescueEmoji(questEmojiRootRef.current);
      }
    };

    runEmojiRescue();

    const frame =
      requestAnimationFrame(runEmojiRescue);

    const timer =
      setTimeout(runEmojiRescue, 120);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [
    loading,
    quests,
    quest,
    actionLoading,
    error,
    notice,
  ]);


  const currentStop = useMemo(() => {
    if (!quest?.progress || quest.progress.status !== "active") {
      return null;
    }

    const order = Number(quest.progress.currentStop);

    return (quest.stops || []).find(
      (stop) =>
        Number(stop?.order) === order &&
        !stop?.locked
    ) || null;
  }, [quest]);


  // SOMA_SNIFF_TEST_V1
  const openDirectionsTo = async (
    destination,
    unavailableMessage
  ) => {
    const latitude = Number(
      destination?.coordinates?.latitude
    );

    const longitude = Number(
      destination?.coordinates?.longitude
    );

    if (
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude)
    ) {
      setError(unavailableMessage);
      return;
    }

    const url =
      `https://maps.apple.com/?daddr=${encodeURIComponent(
        `${latitude},${longitude}`
      )}&dirflg=w`;

    if (typeof onOpenUrl === "function") {
      await onOpenUrl(url);
      return;
    }

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  };


  const openWalkingDirections = async () => {
    await openDirectionsTo(
      currentStop,
      "Walking directions are unavailable for this stop."
    );
  };


  const openFinalBiteDirections = async () => {
    await openDirectionsTo(
      quest?.finalBite,
      "Walking directions are unavailable for the Final Bite."
    );
  };


  const backToCatalog = () => {
    setQuest(null);
    setError("");
    setNotice("");
    loadQuests();
  };


  const status =
    String(quest?.progress?.status || "");

  const isActive =
    status === "active";

  const isCompleted =
    status === "completed";

  const isAbandoned =
    status === "abandoned";


  return (
    <div
      ref={questEmojiRootRef}
      className="party-quest-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Party Quest"
    >
      <div className="party-quest-shell">
        <header className="party-quest-header">
          <div>
            <div className="party-quest-eyebrow">
              The Party Button
            </div>

            <h1>
              🎯 Party Quest
            </h1>

            <p>
              Turn a San Francisco neighborhood into an
              adventure.
            </p>
          </div>

          <button
            type="button"
            className="party-quest-close"
            onClick={onClose}
            aria-label="Close Party Quest"
          >
            ×
          </button>
        </header>


        {error ? (
          <div className="party-quest-message error">
            {error}
          </div>
        ) : null}

        {notice ? (
          <div className="party-quest-message success">
            {notice}
          </div>
        ) : null}


        {!quest ? (
          <>
            <PartyQuestPrize
              backendBase={base}
              partyUid={partyUid}
              mode="catalog"
            />

            {loading ? (
              <div className="party-quest-loading">
                Scouting tonight's quests…
              </div>
            ) : null}

            {!loading && quests.length === 0 ? (
              <div className="party-quest-empty">
                <div>🗺️</div>

                <strong>
                  New quests are being planned
                </strong>

                <p>
                  Check back soon for neighborhood
                  adventures.
                </p>
              </div>
            ) : null}

            {!loading && quests.length > 0 ? (
              <div className="party-quest-list">
                {quests.map((item) => (
                  <button
                    key={item.id || item.slug}
                    type="button"
                    className="party-quest-card"
                    onClick={() => openQuest(item)}
                    disabled={actionLoading}
                  >
                    <div className="party-quest-card-top">
                      <span className="party-quest-badge">
                        {item.badgeEmoji || "🎯"}
                      </span>

                      <span className="party-quest-card-copy">
                        <strong>
                          {item.title}
                        </strong>

                        <small>
                          📍 {item.neighborhood}
                        </small>
                      </span>

                      <span
                        className="party-quest-chevron"
                        aria-hidden="true"
                      >
                        ›
                      </span>
                    </div>

                    <p>
                      {item.description ||
                        "A Party Button neighborhood adventure."}
                    </p>

                    <div className="party-quest-meta">
                      <span>
                        {item.stopCount} stops
                      </span>

                      <span>
                        ~{item.estimatedMinutes} min
                      </span>

                      {item?.progress?.status ===
                      "active" ? (
                        <span className="party-quest-active-pill">
                          In progress
                        </span>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <div className="party-quest-detail">
            <button
              type="button"
              className="party-quest-back"
              onClick={backToCatalog}
            >
              ← All Party Quests
            </button>


            <div className="party-quest-hero">
              <div className="party-quest-hero-badge">
                {quest.badgeEmoji || "🎯"}
              </div>

              <div>
                <div className="party-quest-location">
                  📍 {quest.neighborhood}
                </div>

                <h2>
                  {quest.title}
                </h2>

                <p>
                  {quest.description}
                </p>
              </div>
            </div>


            {quest.progress ? (
              <div className="party-quest-progress">
                <div>
                  <strong>
                    {isCompleted
                      ? "Quest complete"
                      : isAbandoned
                      ? "Quest abandoned"
                      : `Stop ${quest.progress.currentStop} of ${quest.progress.totalStops}`}
                  </strong>

                  <span>
                    {quest.progress.completedStops}/
                    {quest.progress.totalStops} completed
                  </span>
                </div>

                <div className="party-quest-progress-track">
                  <div
                    className="party-quest-progress-fill"
                    style={{
                      width: `${
                        quest.progress.totalStops
                          ? Math.min(
                              100,
                              (quest.progress.completedStops /
                                quest.progress.totalStops) *
                                100
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            ) : null}


            <div className="party-quest-stops">
              {(quest.stops || []).map((stop) => {
                const stopIsCurrent =
                  isActive &&
                  Number(stop.order) ===
                    Number(
                      quest.progress?.currentStop
                    );

                return (
                  <div
                    key={stop.order}
                    className={[
                      "party-quest-stop",
                      stop.locked
                        ? "is-locked"
                        : "",
                      stop.completed
                        ? "is-completed"
                        : "",
                      stopIsCurrent
                        ? "is-current"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div className="party-quest-stop-number">
                      {stop.completed
                        ? "✓"
                        : stop.locked
                        ? "🔒"
                        : stop.order}
                    </div>

                    <div className="party-quest-stop-copy">
                      <strong>
                        {stop.name ||
                          "Mystery Stop"}
                      </strong>

                      {stop.locked ? (
                        <small>
                          Complete the previous stop to
                          reveal this destination.
                        </small>
                      ) : (
                        <>
                          {stop.category ? (
                            <small className="party-quest-category">
                              {String(
                                stop.category
                              ).replace(/_/g, " ")}
                            </small>
                          ) : null}

                          {stop.headline ? (
                            <div className="party-quest-stop-headline">
                              {stop.headline}
                            </div>
                          ) : null}

                          {stop.clue ? (
                            <p>
                              {stop.clue}
                            </p>
                          ) : null}

                          {stop.prompt ? (
                            <div className="party-quest-stop-prompt">
                              {stop.prompt}
                            </div>
                          ) : null}

                          {Array.isArray(stop.sniffNotes) &&
                          stop.sniffNotes.length ? (
                            <div className="party-quest-sniff-notes">
                              {stop.sniffNotes.map((note) => (
                                <span
                                  key={note}
                                  className="party-quest-sniff-note"
                                >
                                  {note}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>


            {!quest.progress ? (
              <button
                type="button"
                className="party-quest-primary"
                onClick={startQuest}
                disabled={actionLoading}
              >
                {actionLoading
                  ? "Starting…"
                  : "Start This Quest"}
              </button>
            ) : null}


            {isActive && currentStop ? (
              <div className="party-quest-current-actions">
                <div className="party-quest-current-label">
                  CURRENT DESTINATION
                </div>

                <strong>
                  {currentStop.name}
                </strong>

                <button
                  type="button"
                  className="party-quest-directions"
                  onClick={openWalkingDirections}
                  disabled={actionLoading}
                >
                  🚶 Walking Directions
                </button>

                <button
                  type="button"
                  className="party-quest-primary"
                  onClick={checkIn}
                  disabled={actionLoading}
                >
                  {actionLoading
                    ? "Checking Location…"
                    : `📍 ${
                        currentStop.checkInLabel ||
                        "Check In"
                      }`}
                </button>

                <div className="party-quest-radius-note">
                  Check in when you're within about 100
                  meters of the stop.
                </div>

                <button
                  type="button"
                  className="party-quest-danger"
                  onClick={abandonQuest}
                  disabled={actionLoading}
                >
                  Abandon Quest
                </button>
              </div>
            ) : null}


            {isCompleted ? (
              <div className="party-quest-complete">
                <div className="party-quest-complete-icon">
                  {quest.badgeEmoji || "🏆"}
                </div>

                <div className="party-quest-complete-label">
                  BADGE UNLOCKED
                </div>

                <strong>
                  {quest.badgeName ||
                    quest.title}
                </strong>

                <p>
                  {quest.completionMessage ||
                    `You completed all ${quest.stopCount} stops.`}
                </p>

                <button
                  type="button"
                  className="party-quest-primary"
                  onClick={restartQuest}
                  disabled={actionLoading}
                >
                  {actionLoading
                    ? "Starting…"
                    : "Play Again"}
                </button>
              </div>
            ) : null}


            {isCompleted && quest.finalBite ? (
              <div className="party-quest-final-bite">
                <div className="party-quest-final-bite-eyebrow">
                  🍴 OPTIONAL FINAL BITE
                </div>

                <strong>
                  {quest.finalBite.name}
                </strong>

                {quest.finalBite.description ? (
                  <p>
                    {quest.finalBite.description}
                  </p>
                ) : null}

                <button
                  type="button"
                  className="party-quest-directions"
                  onClick={openFinalBiteDirections}
                  disabled={actionLoading}
                >
                  🍕 {quest.finalBite.actionLabel ||
                    "Walking Directions"}
                </button>
              </div>
            ) : null}


            {isCompleted ? (
              <PartyQuestPrize
                backendBase={base}
                partyUid={partyUid}
                quest={quest}
                mode="completion"
              />
            ) : null}


            {isAbandoned ? (
              <div className="party-quest-complete">
                <div className="party-quest-complete-icon">
                  ↩️
                </div>

                <strong>
                  Want another shot?
                </strong>

                <p>
                  Restart from Stop 1 whenever you're ready.
                </p>

                <button
                  type="button"
                  className="party-quest-primary"
                  onClick={restartQuest}
                  disabled={actionLoading}
                >
                  Restart Quest
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>


      <style>{`
        .party-quest-overlay{
          position:fixed;
          inset:0;
          z-index:10020;
          overflow-y:auto;
          -webkit-overflow-scrolling:touch;
          background:
            radial-gradient(
              circle at top,
              rgba(236,72,153,.26),
              transparent 38%
            ),
            linear-gradient(
              180deg,
              #08152e 0%,
              #07101e 45%,
              #030712 100%
            );
          color:#fff;
        }

        .party-quest-shell{
          width:min(100%,720px);
          min-height:100%;
          margin:0 auto;
          padding:
            calc(18px + env(safe-area-inset-top))
            16px
            calc(42px + env(safe-area-inset-bottom));
          box-sizing:border-box;
        }

        .party-quest-header{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:14px;
          margin-bottom:18px;
        }

        .party-quest-eyebrow{
          color:#60a5fa;
          font-size:12px;
          font-weight:950;
          letter-spacing:.1em;
          text-transform:uppercase;
        }

        .party-quest-header h1{
          margin:4px 0 0;
          font-size:31px;
          line-height:1.05;
        }

        .party-quest-header p{
          margin:7px 0 0;
          color:#c7d2fe;
          line-height:1.4;
        }

        .party-quest-close{
          width:42px;
          height:42px;
          flex:0 0 42px;
          border-radius:50%;
          border:1px solid rgba(255,255,255,.2);
          background:rgba(255,255,255,.09);
          color:#fff;
          font-size:25px;
        }

        .party-quest-message{
          margin-bottom:14px;
          padding:12px 14px;
          border-radius:14px;
          font-size:14px;
          font-weight:800;
          line-height:1.4;
        }

        .party-quest-message.error{
          background:rgba(239,68,68,.16);
          border:1px solid rgba(248,113,113,.3);
          color:#fecaca;
        }

        .party-quest-message.success{
          background:rgba(45,212,191,.14);
          border:1px solid rgba(94,234,212,.28);
          color:#ccfbf1;
        }

        .party-quest-loading,
        .party-quest-empty{
          margin-top:20px;
          padding:36px 22px;
          text-align:center;
          border-radius:20px;
          background:rgba(255,255,255,.06);
          border:1px solid rgba(255,255,255,.1);
          color:#c7d2fe;
        }

        .party-quest-empty > div{
          font-size:42px;
          margin-bottom:9px;
        }

        .party-quest-empty strong{
          color:#fff;
        }

        .party-quest-empty p{
          margin-bottom:0;
        }

        .party-quest-list{
          display:grid;
          gap:12px;
        }

        .party-quest-card{
          width:100%;
          padding:16px;
          text-align:left;
          color:#fff;
          border-radius:20px;
          border:1px solid rgba(96,165,250,.24);
          background:
            linear-gradient(
              135deg,
              rgba(236,72,153,.18),
              rgba(37,99,235,.18)
            );
          box-shadow:
            0 15px 32px rgba(0,0,0,.2);
        }

        .party-quest-card-top{
          display:flex;
          align-items:center;
          gap:12px;
        }

        .party-quest-badge{
          width:50px;
          height:50px;
          flex:0 0 50px;
          display:grid;
          place-items:center;
          border-radius:16px;
          background:rgba(255,255,255,.1);
          font-size:27px;
        }

        .party-quest-card-copy{
          flex:1;
          min-width:0;
          display:flex;
          flex-direction:column;
        }

        .party-quest-card-copy strong{
          font-size:18px;
        }

        .party-quest-card-copy small{
          margin-top:3px;
          color:#bfdbfe;
          font-weight:800;
        }

        .party-quest-chevron{
          font-size:32px;
          opacity:.7;
        }

        .party-quest-card p{
          margin:13px 0 10px;
          color:#dbeafe;
          line-height:1.4;
        }

        .party-quest-meta{
          display:flex;
          flex-wrap:wrap;
          gap:7px;
          font-size:12px;
          font-weight:900;
          color:#c7d2fe;
        }

        .party-quest-meta > span{
          padding:5px 8px;
          border-radius:999px;
          background:rgba(255,255,255,.08);
        }

        .party-quest-active-pill{
          color:#99f6e4 !important;
          background:rgba(20,184,166,.16) !important;
        }

        .party-quest-back{
          margin-bottom:15px;
          padding:0;
          border:0;
          background:transparent;
          color:#93c5fd;
          font-weight:900;
        }

        .party-quest-hero{
          display:flex;
          gap:15px;
          align-items:flex-start;
          padding:18px;
          border-radius:22px;
          background:
            linear-gradient(
              135deg,
              rgba(236,72,153,.2),
              rgba(37,99,235,.2)
            );
          border:1px solid rgba(255,255,255,.11);
        }

        .party-quest-hero-badge{
          width:58px;
          height:58px;
          flex:0 0 58px;
          display:grid;
          place-items:center;
          border-radius:18px;
          background:rgba(255,255,255,.1);
          font-size:32px;
        }

        .party-quest-location{
          color:#93c5fd;
          font-size:12px;
          font-weight:950;
          text-transform:uppercase;
        }

        .party-quest-hero h2{
          margin:4px 0 7px;
          font-size:24px;
        }

        .party-quest-hero p{
          margin:0;
          color:#dbeafe;
          line-height:1.4;
        }

        .party-quest-progress{
          margin-top:14px;
          padding:14px;
          border-radius:16px;
          background:rgba(255,255,255,.06);
          border:1px solid rgba(255,255,255,.09);
        }

        .party-quest-progress > div:first-child{
          display:flex;
          justify-content:space-between;
          gap:12px;
          font-size:13px;
        }

        .party-quest-progress span{
          color:#a5b4fc;
          font-weight:800;
        }

        .party-quest-progress-track{
          height:8px;
          margin-top:10px;
          overflow:hidden;
          border-radius:999px;
          background:rgba(255,255,255,.1);
        }

        .party-quest-progress-fill{
          height:100%;
          border-radius:999px;
          background:
            linear-gradient(
              90deg,
              #ec4899,
              #3b82f6
            );
          transition:width .25s ease;
        }

        .party-quest-stops{
          display:grid;
          gap:10px;
          margin-top:16px;
        }

        .party-quest-stop{
          display:flex;
          gap:12px;
          padding:14px;
          border-radius:17px;
          background:rgba(255,255,255,.06);
          border:1px solid rgba(255,255,255,.09);
        }

        .party-quest-stop.is-current{
          border-color:rgba(96,165,250,.48);
          background:rgba(37,99,235,.15);
          box-shadow:
            0 0 0 1px rgba(236,72,153,.14);
        }

        .party-quest-stop.is-completed{
          border-color:rgba(45,212,191,.25);
          background:rgba(20,184,166,.09);
        }

        .party-quest-stop.is-locked{
          opacity:.58;
        }

        .party-quest-stop-number{
          width:38px;
          height:38px;
          flex:0 0 38px;
          display:grid;
          place-items:center;
          border-radius:50%;
          background:rgba(255,255,255,.1);
          font-weight:950;
        }

        .party-quest-stop-copy{
          min-width:0;
          flex:1;
        }

        .party-quest-stop-copy strong{
          display:block;
          font-size:16px;
        }

        .party-quest-stop-copy small{
          display:block;
          margin-top:4px;
          color:#a5b4fc;
          line-height:1.35;
        }

        .party-quest-stop-copy p{
          margin:7px 0 0;
          color:#dbeafe;
          font-size:13px;
          line-height:1.4;
        }

        .party-quest-category{
          text-transform:capitalize;
        }

        .party-quest-stop-headline{
          margin-top:10px;
          color:#f9a8d4;
          font-size:11px;
          font-weight:950;
          letter-spacing:.09em;
          text-transform:uppercase;
        }

        .party-quest-stop-prompt{
          margin-top:10px;
          color:#fff;
          font-size:13px;
          font-weight:900;
        }

        .party-quest-sniff-notes{
          display:flex;
          flex-wrap:wrap;
          gap:6px;
          margin-top:8px;
        }

        .party-quest-sniff-note{
          padding:5px 8px;
          border-radius:999px;
          border:1px solid rgba(249,168,212,.23);
          background:rgba(236,72,153,.12);
          color:#fbcfe8;
          font-size:10px;
          font-weight:950;
          letter-spacing:.04em;
        }

        .party-quest-final-bite{
          margin-top:18px;
          padding:18px;
          text-align:center;
          border-radius:20px;
          border:1px solid rgba(251,191,36,.24);
          background:rgba(245,158,11,.1);
        }

        .party-quest-final-bite-eyebrow{
          margin-bottom:6px;
          color:#fcd34d;
          font-size:11px;
          font-weight:950;
          letter-spacing:.08em;
        }

        .party-quest-final-bite > strong{
          display:block;
          font-size:20px;
        }

        .party-quest-final-bite p{
          margin:8px 0 0;
          color:#fde68a;
          font-size:13px;
          line-height:1.4;
        }

        .party-quest-primary,
        .party-quest-directions,
        .party-quest-danger{
          width:100%;
          min-height:48px;
          border-radius:15px;
          font-size:15px;
          font-weight:950;
        }

        .party-quest-primary{
          margin-top:16px;
          border:0;
          color:#fff;
          background:
            linear-gradient(
              90deg,
              #ec4899,
              #2563eb
            );
          box-shadow:
            0 10px 24px rgba(37,99,235,.18);
        }

        .party-quest-directions{
          margin-top:13px;
          border:1px solid rgba(147,197,253,.28);
          background:rgba(59,130,246,.15);
          color:#dbeafe;
        }

        .party-quest-danger{
          margin-top:12px;
          border:1px solid rgba(248,113,113,.23);
          background:transparent;
          color:#fca5a5;
        }

        .party-quest-current-actions{
          margin-top:18px;
          padding:16px;
          border-radius:20px;
          background:rgba(255,255,255,.07);
          border:1px solid rgba(96,165,250,.22);
          text-align:center;
        }

        .party-quest-current-label{
          margin-bottom:4px;
          color:#60a5fa;
          font-size:11px;
          font-weight:950;
          letter-spacing:.08em;
        }

        .party-quest-current-actions > strong{
          display:block;
          font-size:20px;
        }

        .party-quest-radius-note{
          margin-top:8px;
          color:#94a3b8;
          font-size:11px;
          line-height:1.35;
        }

        .party-quest-complete{
          margin-top:18px;
          padding:24px 18px;
          text-align:center;
          border-radius:22px;
          background:
            linear-gradient(
              135deg,
              rgba(236,72,153,.17),
              rgba(37,99,235,.17)
            );
          border:1px solid rgba(255,255,255,.11);
        }

        .party-quest-complete-icon{
          font-size:48px;
        }

        .party-quest-complete-label{
          margin:7px 0 3px;
          color:#93c5fd;
          font-size:11px;
          font-weight:950;
          letter-spacing:.1em;
        }

        .party-quest-complete > strong{
          display:block;
          font-size:22px;
        }

        .party-quest-complete p{
          color:#c7d2fe;
        }

        button:disabled{
          opacity:.55;
          cursor:not-allowed;
        }
      `}</style>
    </div>
  );
}
